import { createFileRoute } from "@tanstack/react-router";
import { Phone, ShieldCheck, Award, Users, MapPin, Heart, Star, Clock } from "lucide-react";
import { openQuoteModal } from "../components/quote-modal";
import { Reveal } from "../components/reveal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { SHOW_LICENSED_INSURED } from "../data/flags";
import { InternalLinkRow, POPULAR_SERVICE_LINKS, CORE_CITY_LINKS } from "@/components/internal-links";
import { InlineQuoteSection } from "@/components/inline-quote-section";

const SITE = "https://capitalpropressurewashing.com";
const TITLE = "About Capital Pro Pressure Washing | 10+ Years in the Capital Region";
const DESC =
  "Capital Pro Pressure Washing has served Albany and the Capital Region for over 10 years. Licensed and insured, military and first responder discounts, professional soft-wash and pressure cleaning.";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Licensed & Insured",
    desc: "Every job is covered. Your home and property are protected from start to finish — we carry full liability insurance on every project.",
  },
  {
    icon: Award,
    title: "10+ Years Experience",
    desc: "Over a decade of cleaning Capital Region homes. We've seen every siding type, roof pitch, and stain pattern this area throws at you.",
  },
  {
    icon: Heart,
    title: "Military & First Responder Discounts",
    desc: "We're proud to offer discounts for the people who serve our community and country. Just mention it when you reach out.",
  },
  {
    icon: Users,
    title: "Local Crew, Not a Franchise",
    desc: "We live and work in the Capital Region. You talk to the owner, not a call center. The same crew that quotes your job does the work.",
  },
];

const TIMELINE = [
  { year: "10+ Years", title: "Serving the Capital Region", desc: "From Albany to Saratoga Springs, we've built our reputation one clean home at a time." },
  { year: "90+ Reviews", title: "Trusted by Homeowners", desc: "Our Google reviews tell the story — real customers, real results, real five-star feedback." },
  { year: "13 Services", title: "Full Exterior Care", desc: "House washing, roof cleaning, gutters, concrete, pavers, decks, windows, lighting, and more — all from one trusted crew." },
  { year: "12+ Towns", title: "Across the Capital Region", desc: "Albany, Schenectady, Saratoga Springs, Troy, Clifton Park, Colonie, Latham, and surrounding areas." },
];

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/about` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: `${SITE}/about` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Capital Pro Pressure Washing",
          url: SITE,
          telephone: "+15189001913",
          priceRange: "$$",
          image: `${SITE}/__l5e/assets-v1/7fb348af-f23e-48db-88cd-6aeda94adc1a/hero-crew-surface-cleaner.jpg`,
          areaServed: "Capital Region, NY",
          address: {
            "@type": "PostalAddress",
            streetAddress: "5A Vatrano Rd",
            addressLocality: "Albany",
            addressRegion: "NY",
            postalCode: "12205",
            addressCountry: "US",
          },
          ...(SHOW_LICENSED_INSURED ? { description: "Licensed and insured pressure washing serving Albany and the Capital Region for over 10 years." } : { description: "Pressure washing serving Albany and the Capital Region for over 10 years." }),
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "About" }]} />

      {/* Hero */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <p
            className="font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)", color: "#ff6b7f" }}
          >
            Capital Region, NY
          </p>
          <h1
            className="text-3xl md:text-5xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            About Capital Pro
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            We're a local pressure washing crew that's been cleaning homes and businesses
            across the Capital Region for over 10 years. No franchises, no call centers —
            just honest work and results that last.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Get Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[900px] px-4">
          <div className="text-center mb-8">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Our Story
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Built on Clean Work
            </h2>
          </div>
          <div className="space-y-5 text-[var(--brand-charcoal)]/85 text-base md:text-lg leading-relaxed">
            <p>
              Capital Pro Pressure Washing started with a simple idea: do the job right,
              treat every home like it's your own, and let the results speak for themselves.
              Over a decade later, that's still how we operate.
            </p>
            <p>
              We know the Capital Region. We know that Hudson River humidity and dense tree
              cover in Pine Hills and Buckingham Pond grow mildew fast. We know that Saratoga
              Springs winters leave salt and grime on driveways, and that Clifton Park
              Colonials collect green algae on the north side. We've cleaned it all — and we
              know the right method for every surface.
            </p>
            <p>
              We're not the cheapest option in town, and we don't try to be. We use
              commercial-grade equipment, professional detergents, and the right pressure for
              each surface. Our soft-wash system kills mold and algae at the root so your
              home stays cleaner, longer. That's the difference between a wash that lasts a
              few months and one that lasts years.
            </p>
            <p>
              When you call Capital Pro, you talk to the owner. The same crew that quotes your
              job does the work. No subcontractors, no surprises. Just clean results and a
              home you're proud to pull up to.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <div className="text-center mb-8 md:mb-10">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Why Homeowners Choose Us
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              What Sets Us Apart
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal
                  key={v.title}
                  delayMs={i * 70}
                  className="card-pop bg-white rounded-lg p-6 border border-gray-200"
                >
                  <div className="w-11 h-11 rounded-md bg-[var(--brand-red)]/10 text-[var(--brand-red)] flex items-center justify-center mb-4">
                    <Icon size={22} />
                  </div>
                  <h3
                    className="text-[var(--brand-deep-blue)] text-lg uppercase mb-2 leading-tight"
                    style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                  >
                    {v.title}
                  </h3>
                  <p className="text-[var(--brand-charcoal)]/85 text-sm leading-relaxed">
                    {v.desc}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats / Timeline */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <div className="text-center mb-8 md:mb-10">
            <p
              className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-2"
              style={{ fontFamily: "var(--font-display)" }}
            >
              By the Numbers
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              A Decade of Clean
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIMELINE.map((t, i) => (
              <Reveal
                key={t.title}
                delayMs={i * 70}
                className="card-pop bg-[var(--brand-light-gray)] rounded-lg p-6 border border-gray-200 text-center"
              >
                <p
                  className="text-[var(--brand-red)] text-2xl md:text-3xl font-bold leading-tight"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {t.year}
                </p>
                <h3
                  className="mt-2 text-[var(--brand-deep-blue)] text-base uppercase leading-tight"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {t.title}
                </h3>
                <p className="mt-2 text-[var(--brand-charcoal)]/75 text-sm leading-relaxed">
                  {t.desc}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-[var(--brand-light-gray)] py-10 md:py-12">
        <div className="mx-auto max-w-[1000px] px-4">
          <Reveal className="rounded-lg bg-white border border-gray-200 p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={20} className="text-[#fbbf24]" fill="#fbbf24" />
                  ))}
                </div>
                <span className="text-[var(--brand-charcoal)] font-semibold text-sm">
                  90+ Google Reviews
                </span>
              </div>
              <div className="hidden md:block w-px h-8 bg-gray-200" />
              {SHOW_LICENSED_INSURED && (
                <div className="inline-flex items-center gap-2 text-[var(--brand-charcoal)] font-semibold text-sm">
                  <ShieldCheck size={20} className="text-[var(--brand-deep-blue)]" />
                  Licensed & Insured
                </div>
              )}
              <div className="hidden md:block w-px h-8 bg-gray-200" />
              <div className="inline-flex items-center gap-2 text-[var(--brand-charcoal)] font-semibold text-sm">
                <Clock size={20} className="text-[var(--brand-deep-blue)]" />
                10+ Years Local
              </div>
              <div className="hidden md:block w-px h-8 bg-gray-200" />
              <div className="inline-flex items-center gap-2 text-[var(--brand-charcoal)] font-semibold text-sm">
                <MapPin size={20} className="text-[var(--brand-deep-blue)]" />
                Capital Region, NY
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-12">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <h2
            className="text-2xl md:text-3xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Let's Get Your Home Clean
          </h2>
          <p className="mt-2 text-white/85">
            Free quotes, no obligation. Tell us what you need — we'll take it from there.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => openQuoteModal()}
              className="inline-flex items-center justify-center rounded-md bg-[#C8102E] text-white px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Get Free Quote
            </button>
            <a
              href="tel:+15189001913"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>
      <InternalLinkRow
        eyebrow="Explore The Site"
        title="Popular Services"
        links={POPULAR_SERVICE_LINKS}
      />

      <InternalLinkRow
        tone="white"
        eyebrow="Where We Work"
        title="Areas We Serve"
        links={CORE_CITY_LINKS}
      />

      <InlineQuoteSection source="About" />
    </main>
  );
}
